/* eslint-disable @typescript-eslint/no-extraneous-class */
import {
  loadLogger,
  Logger,
  LoggerEnvironmentConfig,
  LogMessage,
} from '@vanguard/common-logging-client';
import { getBrowserContextData } from './getBrowserContextData';
import { redactPIIFromPayload } from './pii-redactor';
import { getCurrentTraceContext } from './trace-context';

type WebLoggerInitOptions = LoggerEnvironmentConfig & {
  isProduction?: boolean;
};

const baseLoggerConfig: Pick<LogMessage, 'appCode'> = { appCode: 'WAH' };
const DEFAULT_LOG_LEVEL = 'info'

type WebLoggerLogOptions = {
  level?: keyof Logger;
  payload?: any;
} & Omit<LogMessage, 'appCode' | 'payload'>;

export class WebLogger {
  private static _LoggerPromise?: Promise<Logger | undefined>;
  private static basePayloadProperties: Record<string, any> | undefined;
  private static isProduction: boolean = true;

  /**
   * Initialize the WebLogger instance
   *
   * This will fetch the logger, initialize it, and log contextual
   * information from the browser/app
   */
  static async init(loggerConfig: WebLoggerInitOptions) {
    // Store production flag for PII redaction
    WebLogger.isProduction = loggerConfig.isProduction ?? true;

    WebLogger._LoggerPromise = loadLogger(loggerConfig)
      .then((logger) => {
        logger.info({
          ...baseLoggerConfig,
          message: 'Initialized WebLogger',
          payload: {
            ...getBrowserContextData(),
            ...WebLogger.basePayloadProperties,
          },
          skipBatch: true,
        });
        return logger;
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize WebLogger...', error);
        return undefined;
      });
  }

  private static async _log({ level = DEFAULT_LOG_LEVEL, ...event }: WebLoggerLogOptions) {
    const logger = WebLogger._LoggerPromise ? await WebLogger._LoggerPromise : undefined;

    if (!logger) {
      // eslint-disable-next-line no-console
      console.error('WebLogger not initialized, cannot log message', event);
      return;
    }

    try {
      const serializedPayload = event.payload ? JSON.parse(JSON.stringify(event.payload)) : undefined;

      // Redact PII in prod
      const processedPayload = redactPIIFromPayload(serializedPayload, WebLogger.isProduction);

      // Extract current trace context for log-trace correlation
      const traceContext = getCurrentTraceContext();

      const logPayload = {
        ...baseLoggerConfig,
        ...event,
        payload: {
          ...WebLogger.basePayloadProperties,
          ...processedPayload,
          // Add trace context if available (enables Splunk → Honeycomb correlation)
          ...(traceContext && {
            trace_id: traceContext.traceId,
            span_id: traceContext.spanId,
          }),
        },
      };
      // Sends log to console
      if (logPayload.payload && Object.keys(logPayload.payload).length > 0) {
        console.groupCollapsed(`[${level.toUpperCase()}] ${logPayload.message || 'No message'}`);
        console.log('Payload:', logPayload.payload);
        console.groupEnd();
      } else {
        console.log(`[${level.toUpperCase()}] ${logPayload.message || 'No message'}`);
      }

      logger[level](logPayload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to log message via WebLogger...', error);
    }
  }

  /**
   * A method to log all events using the WebLogger
   *
   * Level defaults to `info` if not provided
   *
   * Never throws; errors are caught and logged to console
   */
  static log(args: WebLoggerLogOptions) {
    void WebLogger._log(args);
  }

  /**
   * In rare circumstances it may be preferable to wait for the logging to complete
   * before continuing execution.
   */
  static async logAsync(args: WebLoggerLogOptions) {
    await WebLogger._log(args);
  }

  /**
   * Optional method for setting a correlation group ID for a users session
   */
  static setCorrelationGroupId(correlationGroupId: string) {
    WebLogger.basePayloadProperties = {
      correlationGroupId,
    };
  }

  /**
   * Set global context for all subsequent logs
   *
   * This merges context fields into basePayloadProperties, which are
   * automatically included in every log entry.
   *
   * Use this for persistent context like POID, service name, environment, etc.
   * that should appear in all logs for a session.
   *
   * @example
   * WebLogger.setContext({
   *   poid: '12345',
   *   service: 'pwi',
   *   environment: 'eng'
   * });
   */
  static setContext(context: Record<string, any>): void {
    WebLogger.basePayloadProperties = {
      ...WebLogger.basePayloadProperties,
      ...context,
    };
  }

  /**
   * Clear all context except correlationGroupId
   *
   * Useful when user logs out or switches context.
   * Preserves correlationGroupId if it was set.
   */
  static clearContext(): void {
    const correlationGroupId = WebLogger.basePayloadProperties?.['correlationGroupId'];
    WebLogger.basePayloadProperties = correlationGroupId
      ? { correlationGroupId }
      : undefined;
  }

  /**
   * Get current context
   *
   * Returns a copy of the current basePayloadProperties
   */
  static getContext(): Record<string, any> | undefined {
    return WebLogger.basePayloadProperties
      ? { ...WebLogger.basePayloadProperties }
      : undefined;
  }
}
